import React from 'react';

import classNames from 'classnames';
import {
  Box,
  Button,
  HStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList
} from '@chakra-ui/react';
import { BiomeIcon, IconName } from '@codametrix/verdigris';

export type ProcessSelectionProps = {
  caseRuns: {
    [key: string]: CMxAPI.CaseRun[];
  };
  activeId: number;
  onClick: (event: React.MouseEvent) => void;
} & React.HTMLAttributes<HTMLAnchorElement>;

const ProcessSelection: React.FC<ProcessSelectionProps> = (
  props: ProcessSelectionProps
) => {
  const { caseRuns, activeId, onClick } = props;
  const multipleRunTypes =
    Object.keys(caseRuns).filter(key => caseRuns[key].length > 0).length > 1;

  return (
    <HStack
      className="btn-group btn-group-block process-selection"
      spacing={2}
      wrap="wrap"
      align="center">
      {Object.keys(caseRuns).map(runType => {
        const display = runType === 'prediction' ? 'Final' : runType;
        if (caseRuns[runType].length === 0) {
          return null;
        }

        const runs = [...caseRuns[runType]].sort(
          (a: CMxAPI.CaseRun, b: CMxAPI.CaseRun) =>
            a.process_instance_id - b.process_instance_id
        );
        const activeIndex = runs.findIndex(
          run => run.process_instance_id === activeId
        );
        const classes = classNames([
          runType,
          {
            'multiple-run-types': multipleRunTypes
          }
        ]);

        const triggerActive = activeIndex !== -1;

        return (
          <Box className={classes} key={`${runType}`}>
            {runs.length === 1 ? (
              <Button
                as="a"
                href="#/new-process"
                className={classNames(`qa-${runType}-button`, {
                  active: triggerActive
                })}
                variant="secondary"
                size="sm"
                data-process-id={runs[0].process_instance_id}
                onClick={onClick}
                isActive={triggerActive}>
                {display}
              </Button>
            ) : (
              <Menu placement="bottom-end" gutter={4}>
                <MenuButton
                  as={Button}
                  className={classNames(`qa-${runType}-dropdown`, {
                    active: triggerActive
                  })}
                  variant="secondary"
                  size="sm"
                  isActive={triggerActive}
                  rightIcon={<BiomeIcon iconName={IconName.Chevron_Down} />}>
                  {display}
                  {activeIndex >= 0 ? ` ${activeIndex + 1}` : ''}
                </MenuButton>
                <MenuList
                  zIndex={9999}
                  maxH="50vh"
                  overflowY="auto"
                  sx={{
                    color: 'gray.800',
                    '& .chakra-menu__menuitem': {
                      color: 'gray.800'
                    },
                    '& .chakra-menu__menuitem:hover, & .chakra-menu__menuitem[data-focus], & .chakra-menu__menuitem:focus-visible':
                      {
                        color: 'gray.900'
                      }
                  }}>
                  {runs.map((run, idx) => {
                    const itemActive = run.process_instance_id === activeId;
                    return (
                      <MenuItem
                        key={`${runType}-${idx}`}
                        as="a"
                        href="#/new-process"
                        data-testid={`${runType}-${idx}-button`}
                        className={classNames(`qa-${runType}-${idx}-button`, {
                          active: itemActive
                        })}
                        data-process-id={run.process_instance_id}
                        onClick={onClick}
                        bg={itemActive ? 'gray.100' : undefined}
                        fontWeight={itemActive ? 'semibold' : undefined}>
                        {`${display} ${idx + 1}`}
                      </MenuItem>
                    );
                  })}
                </MenuList>
              </Menu>
            )}
          </Box>
        );
      })}
    </HStack>
  );
};
ProcessSelection.displayName = 'ProcessSelection';

export { ProcessSelection };
